import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../../components/Breadcrumb';
import {
  deleteAnalyticsReport,
  getAnalyticsOverview,
  getAnalyticsReportStatus,
  getCostEfficiency,
  getFeatureUsage,
  getInterventionHeatmap,
  listAnalyticsReports,
  triggerAnalyticsReport,
} from '../../api/analytics';
import type {
  AnalyticsOverview,
  AnalyticsPeriod,
  AnalyticsReportSummary,
  CostEfficiency,
  FeatureUsageRate,
  HeatmapEntry,
} from '../../types/analytics';
import { stageInfo, stageOrder, type StageInfo } from './analyticsStages';
import { AnalyticsPollTimeoutError, pollAnalyticsReport } from './analyticsPolling';
import './Analytics.css';

const REPORT_PAGE_SIZE = 10;
const RANGE_OPTIONS = [7, 14, 30] as const;

type StatusKind = 'idle' | 'info' | 'success' | 'warning' | 'error';
interface StatusMessage { kind: StatusKind; text: string }
interface CardErrors {
  overview?: string;
  heatmap?: string;
  cost?: string;
  features?: string;
  reports?: string;
}

const IDLE_STATUS: StatusMessage = { kind: 'idle', text: '' };

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function periodLabel(period: AnalyticsPeriod | null): string {
  if (!period) return '—';
  const start = period.start.slice(0, 10);
  const parts = period.end.slice(0, 10).split('-').map(Number);
  const endDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  endDate.setUTCDate(endDate.getUTCDate() - 1);
  return `${start} – ${endDate.toISOString().slice(0, 10)}`;
}

function reportPeriodLabel(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  return periodLabel({ start, end, days: 0 });
}

export default function AnalyticsDashboard() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const rangeRequestRef = useRef(0);
  const pollAbortRef = useRef<AbortController | null>(null);

  const [selectedDays, setSelectedDays] = useState(7);
  const [appliedPeriod, setAppliedPeriod] = useState<AnalyticsPeriod | null>(null);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapEntry[]>([]);
  const [heatmapDates, setHeatmapDates] = useState<string[]>([]);
  const [cost, setCost] = useState<CostEfficiency | null>(null);
  const [features, setFeatures] = useState<FeatureUsageRate[]>([]);
  const [errors, setErrors] = useState<CardErrors>({});
  const [rangeLoading, setRangeLoading] = useState(true);
  const [featuresLoading, setFeaturesLoading] = useState(true);

  const [reports, setReports] = useState<AnalyticsReportSummary[]>([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportPage, setReportPage] = useState(0);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<StatusMessage>(IDLE_STATUS);

  const setCardError = useCallback((key: keyof CardErrors, value?: string) => {
    setErrors((current) => ({ ...current, [key]: value }));
  }, []);

  const loadRange = useCallback(async (days: number) => {
    const requestId = ++rangeRequestRef.current;
    setRangeLoading(true);
    const [overviewResult, heatmapResult, costResult] = await Promise.allSettled([
      getAnalyticsOverview(days),
      getInterventionHeatmap(days),
      getCostEfficiency(days),
    ]);
    if (!mountedRef.current || requestId !== rangeRequestRef.current) return;

    let nextPeriod: AnalyticsPeriod | null = null;
    if (overviewResult.status === 'fulfilled') {
      setOverview(overviewResult.value);
      setCardError('overview');
      nextPeriod = overviewResult.value.period;
    } else {
      setCardError('overview', errorText(overviewResult.reason));
    }
    if (heatmapResult.status === 'fulfilled') {
      setHeatmap(heatmapResult.value.data ?? []);
      setHeatmapDates(heatmapResult.value.dates ?? []);
      setCardError('heatmap');
      nextPeriod = nextPeriod ?? heatmapResult.value.period;
    } else {
      setCardError('heatmap', errorText(heatmapResult.reason));
    }
    if (costResult.status === 'fulfilled') {
      setCost(costResult.value);
      setCardError('cost');
      nextPeriod = nextPeriod ?? costResult.value.period;
    } else {
      setCardError('cost', errorText(costResult.reason));
    }
    if (nextPeriod) setAppliedPeriod(nextPeriod);
    setRangeLoading(false);
  }, [setCardError]);

  const loadFeatures = useCallback(async () => {
    setFeaturesLoading(true);
    try {
      const value = await getFeatureUsage();
      if (!mountedRef.current) return;
      setFeatures(value ?? []);
      setCardError('features');
    } catch (error) {
      if (mountedRef.current) setCardError('features', errorText(error));
    } finally {
      if (mountedRef.current) setFeaturesLoading(false);
    }
  }, [setCardError]);

  const loadReports = useCallback(async (page: number) => {
    setReportsLoading(true);
    try {
      const value = await listAnalyticsReports(page * REPORT_PAGE_SIZE, REPORT_PAGE_SIZE);
      if (!mountedRef.current) return;
      setReports(value.data ?? []);
      setReportTotal(value.total ?? 0);
      setReportPage(page);
      setCardError('reports');
    } catch (error) {
      if (mountedRef.current) setCardError('reports', errorText(error));
    } finally {
      if (mountedRef.current) setReportsLoading(false);
    }
  }, [setCardError]);

  const beginPolling = useCallback(async (reportId: string) => {
    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;
    setGenerating(true);
    setStatusMessage({ kind: 'info', text: t('analytics.reportGenerating') });
    try {
      const finished = await pollAnalyticsReport(reportId, getAnalyticsReportStatus, {
        signal: controller.signal,
      });
      if (!mountedRef.current || controller.signal.aborted) return;
      if (finished.status === 'completed') {
        setStatusMessage({ kind: 'success', text: t('analytics.reportGenerated') });
      } else if (finished.status === 'partial') {
        setStatusMessage({ kind: 'warning', text: t('analytics.reportGeneratedPartial') });
      } else {
        setStatusMessage({ kind: 'error', text: t('analytics.reportFailed') });
      }
      await loadReports(reportPage);
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      if (error instanceof AnalyticsPollTimeoutError) {
        setStatusMessage({ kind: 'error', text: t('analytics.reportTimeout') });
      } else {
        setStatusMessage({ kind: 'error', text: t('analytics.reportPollingFailed') });
      }
    } finally {
      if (mountedRef.current && !controller.signal.aborted) setGenerating(false);
    }
  }, [loadReports, reportPage, t]);

  useEffect(() => {
    mountedRef.current = true;
    void loadRange(7);
    void loadFeatures();
    void loadReports(0);
    return () => {
      mountedRef.current = false;
      pollAbortRef.current?.abort();
    };
  }, [loadFeatures, loadRange, loadReports]);

  useEffect(() => {
    const running = reports.find((report) => report.status === 'generating');
    if (running && !generating) void beginPolling(running.id);
  }, [beginPolling, generating, reports]);

  const handleRangeChange = (days: number) => {
    setSelectedDays(days);
    void loadRange(days);
  };

  const handleGenerate = async () => {
    if (!appliedPeriod) {
      setStatusMessage({ kind: 'error', text: t('analytics.rangeUnavailable') });
      return;
    }
    setGenerating(true);
    setStatusMessage({ kind: 'info', text: t('analytics.reportTriggered') });
    try {
      const response = await triggerAnalyticsReport(appliedPeriod.start, appliedPeriod.end);
      void beginPolling(response.reportId);
    } catch (error) {
      setGenerating(false);
      setStatusMessage({ kind: 'error', text: t('analytics.reportTriggerFailed', { error: errorText(error) }) });
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm(t('analytics.archiveConfirm'))) return;
    setDeletingId(id);
    try {
      await deleteAnalyticsReport(id);
      await loadReports(reportPage);
    } catch (error) {
      setStatusMessage({ kind: 'error', text: t('analytics.archiveFailed', { error: errorText(error) }) });
    } finally {
      if (mountedRef.current) setDeletingId(null);
    }
  };

  const appliedLabel = periodLabel(appliedPeriod);
  const reportPageCount = Math.max(1, Math.ceil(reportTotal / REPORT_PAGE_SIZE));
  const statusClass = `analytics-status analytics-status--${statusMessage.kind}`;

  return (
    <main className="container analytics-page">
      <Breadcrumb items={[{ label: t('analytics.breadcrumbHome'), to: '/projects' }, { label: 'Analytics' }]} />
      <div className="page-header analytics-page__header">
        <div>
          <h1 className="page-title">{t('analytics.title')}</h1>
          <p className="analytics-period" data-testid="applied-period">
            {t('analytics.appliedPeriod', { period: appliedLabel })}
            {rangeLoading && <span> · {t('analytics.updatingRange')}</span>}
          </p>
        </div>
        <div className="analytics-range" aria-label={t('analytics.rangeSelector')}>
          {RANGE_OPTIONS.map((days) => (
            <button key={days} className={`btn btn-sm ${selectedDays === days ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={selectedDays === days} onClick={() => handleRangeChange(days)}>
              {t('analytics.rangeDays', { days })}
            </button>
          ))}
        </div>
      </div>

      <section className="analytics-section" aria-labelledby="automation-health-title">
        <h2 id="automation-health-title" className="section-title">{t('analytics.automationHealth')}</h2>
        {errors.overview ? (
          <CardError title={t('analytics.overviewLoadFailed')} detail={errors.overview} />
        ) : overview ? (
          <>
            <div className="analytics-metric-grid" data-testid="overview-card">
              <MetricCard
                label={t('analytics.firstPassRate')}
                value={overview.first_pass_eligible > 0
                  ? `${(overview.overall_first_pass_rate * 100).toFixed(1)}%`
                  : t('analytics.collectingData')}
                variant={overview.first_pass_eligible === 0 ? undefined
                  : overview.overall_first_pass_rate > 0.7 ? 'success'
                    : overview.overall_first_pass_rate > 0.5 ? 'warning' : 'danger'}
                tooltip={t('analytics.firstPassRateTooltip', {
                  eligible: overview.first_pass_eligible,
                  since: overview.first_pass_metric_since?.slice(0, 10) || t('analytics.metricSinceUnavailable'),
                })}
              />
              <MetricCard label={t('analytics.interventionDensity')}
                value={`${overview.intervention_density.toFixed(2)} ${t('analytics.densityUnit')}`}
                variant={overview.intervention_density > 2 ? 'danger' : overview.intervention_density > 1 ? 'warning' : 'success'}
                tooltip={t('analytics.interventionDensityTooltip')} />
              <MetricCard label={t('analytics.totalInterventions')}
                value={String(overview.total_interventions)}
                tooltip={t('analytics.totalInterventionsTooltip', { days: appliedPeriod?.days ?? selectedDays })} />
            </div>
            {overview.event_pipeline && overview.event_pipeline.dropped > 0 && (
              <div role="alert" className="analytics-inline-warning">
                {t('analytics.eventPipelineDrops', { count: overview.event_pipeline.dropped })}
              </div>
            )}
          </>
        ) : <CardLoading />}

        {errors.heatmap ? (
          <CardError title={t('analytics.heatmapLoadFailed')} detail={errors.heatmap} />
        ) : heatmapDates.length > 0 ? (
          <InterventionHeatmap data={heatmap} dates={heatmapDates} stageInfoMap={stageInfo} t={t} />
        ) : rangeLoading ? <CardLoading /> : <EmptyCard text={t('analytics.noData')} />}
      </section>

      <section className="analytics-two-column analytics-section">
        <div>
          <h2 className="section-title">{t('analytics.featureUsage')}</h2>
          {errors.features ? <CardError title={t('analytics.featureLoadFailed')} detail={errors.features} />
            : featuresLoading ? <CardLoading />
              : <FeatureUsageCard features={features} t={t} />}
        </div>
        <div>
          <h2 className="section-title">{t('analytics.costEfficiency')}</h2>
          {errors.cost ? <CardError title={t('analytics.costLoadFailed')} detail={errors.cost} />
            : cost ? <CostCards cost={cost} t={t} /> : <CardLoading />}
        </div>
      </section>

      <section className="analytics-section" aria-labelledby="analytics-report-title">
        <div className="analytics-report-heading">
          <h2 id="analytics-report-title" className="section-title">
            {t('analytics.aiReport')}
            <AccessibleTooltip text={t('analytics.aiReportTooltip')} label={t('analytics.aiReportHelp')} />
          </h2>
          <div className="analytics-report-actions">
            {statusMessage.text && <span role="status" className={statusClass}>{statusMessage.text}</span>}
            <button data-testid="generate-report" className="btn btn-primary" onClick={handleGenerate}
              disabled={generating || !appliedPeriod}>
              {generating ? t('analytics.analyzing') : t('analytics.generateReport')}
            </button>
          </div>
        </div>

        {errors.reports && <CardError title={t('analytics.reportListLoadFailed')} detail={errors.reports} />}
        <div className="card analytics-table-card">
          {reportsLoading ? <CardLoading bare /> : reports.length === 0 ? (
            <div className="empty-state"><p>{t('analytics.noReports')}</p></div>
          ) : (
            <div className="analytics-table-scroll">
              <table className="analytics-report-table">
                <thead><tr>
                  <th>{t('analytics.timeColumn')}</th>
                  <th>{t('analytics.reportRangeColumn')}</th>
                  <th>{t('analytics.triggerType')}</th>
                  <th>{t('analytics.statusColumn')}</th>
                  <th>{t('analytics.actionsColumn')}</th>
                </tr></thead>
                <tbody>{reports.map((report) => {
                  const terminal = ['completed', 'partial', 'failed', 'cancelled'].includes(report.status);
                  return (
                    <tr key={report.id}>
                      <td><span className="analytics-report-id">{report.id}</span><br />{new Date(report.createdAt).toLocaleString()}</td>
                      <td>{reportPeriodLabel(report.timeRangeStart, report.timeRangeEnd)}</td>
                      <td>{report.triggerType.startsWith('auto') ? t('analytics.auto') : t('analytics.manual')}</td>
                      <td><ReportStatusBadge status={report.status} t={t} /></td>
                      <td><div className="analytics-row-actions">
                        {(report.status === 'completed' || report.status === 'partial') && (
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/admin/analytics/report/${report.id}`)}>
                            {t('analytics.view')}
                          </button>
                        )}
                        {terminal && (
                          <button data-testid={`archive-${report.id}`} className="btn btn-ghost btn-sm analytics-danger-action"
                            disabled={deletingId === report.id} onClick={() => handleArchive(report.id)}>
                            {deletingId === report.id ? t('analytics.archiving') : t('analytics.archive')}
                          </button>
                        )}
                      </div></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
          <div className="analytics-pagination">
            <span>{t('analytics.reportPagination', { page: reportPage + 1, totalPages: reportPageCount, total: reportTotal })}</span>
            <div>
              <button className="btn btn-ghost btn-sm" disabled={reportPage === 0 || reportsLoading}
                onClick={() => void loadReports(reportPage - 1)}>{t('analytics.previousPage')}</button>
              <button data-testid="report-next" className="btn btn-ghost btn-sm"
                disabled={(reportPage + 1) * REPORT_PAGE_SIZE >= reportTotal || reportsLoading}
                onClick={() => void loadReports(reportPage + 1)}>{t('analytics.nextPage')}</button>
            </div>
          </div>
        </div>
      </section>

      <div className="analytics-presentation-link">
        <a href="/analytics-presentation.html" target="_blank" rel="noopener noreferrer">
          {t('analytics.viewPresentation')} <span aria-hidden="true">↗</span>
        </a>
      </div>
    </main>
  );
}

function MetricCard({ label, value, variant, tooltip }: {
  label: string;
  value: string;
  variant?: 'success' | 'warning' | 'danger';
  tooltip?: string;
}) {
  return (
    <div className={`card analytics-metric-card ${variant ? `analytics-metric-card--${variant}` : ''}`}>
      <div className="analytics-metric-card__label">
        {label}
        {tooltip && <AccessibleTooltip text={tooltip} label={`${label} help`} />}
      </div>
      <div className="analytics-metric-card__value">{value}</div>
    </div>
  );
}

function CostCards({ cost, t }: { cost: CostEfficiency; t: (key: string, options?: Record<string, unknown>) => string }) {
  return (
    <div className="analytics-cost-grid">
      <MetricCard label={t('analytics.creditsPerSec')} value={(cost.credits_per_output_second / 100).toFixed(4)} />
      <MetricCard label={t('analytics.totalCredits')} value={(cost.total_credits / 100).toFixed(2)} />
      <MetricCard label={t('analytics.totalCost')} value={`¥${cost.total_cost.toFixed(2)}`} />
      <MetricCard label={t('analytics.totalSeconds')} value={String(cost.total_video_seconds)} />
    </div>
  );
}

function FeatureUsageCard({ features, t }: {
  features: FeatureUsageRate[];
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (features.length === 0) return <EmptyCard text={t('analytics.noData')} />;
  return (
    <div className="card analytics-feature-card">
      {features.map((feature) => {
        const enabled = feature.enabled ?? feature.used ?? 0;
        const eligible = feature.eligible ?? feature.total ?? 0;
        return (
          <div key={feature.feature} className="analytics-feature-row">
            <div><span>{t(`analytics.features.${feature.feature}`)}</span><span>{(feature.rate * 100).toFixed(0)}% ({enabled}/{eligible})</span></div>
            <div className="analytics-progress"><span style={{ width: `${Math.min(100, feature.rate * 100)}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function InterventionHeatmap({ data, dates, stageInfoMap, t }: {
  data: HeatmapEntry[];
  dates: string[];
  stageInfoMap: Record<string, StageInfo>;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const stages = useMemo(() => {
    const found = new Set(data.map((entry) => entry.stage));
    const ordered = stageOrder.filter((stage) => found.has(stage));
    found.forEach((stage) => { if (!ordered.includes(stage)) ordered.push(stage); });
    return ordered;
  }, [data]);
  const lookup = useMemo(() => new Map(data.map((entry) => [`${entry.stage}|${entry.date}`, entry])), [data]);
  const maxCount = Math.max(1, ...data.filter((entry) => !entry.hasRate).map((entry) => entry.count));
  const countIntensity = (count: number) => Math.log1p(count) / Math.log1p(maxCount);
  const rateIntensity = (rate: number) => Math.max(0, rate) / (Math.max(0, rate) + 0.5);
  const fmtDate = (date: string) => date.slice(5).replace('-', '/');

  return (
    <div className="card analytics-heatmap-card">
      <h3>{t('analytics.heatmapTitle')}</h3>
      {stages.length === 0 ? <p className="analytics-muted">{t('analytics.noData')}</p> : (
        <div className="analytics-heatmap-scroll">
          <div role="grid" className="analytics-heatmap" style={{ gridTemplateColumns: `minmax(140px, 1.5fr) repeat(${dates.length}, minmax(54px, 1fr))` }}>
            <div />
            {dates.map((date) => <div role="columnheader" key={date}>{fmtDate(date)}</div>)}
            {stages.map((stage) => {
              const info = stageInfoMap[stage];
              return (
                <React.Fragment key={stage}>
                  <div role="rowheader" className="analytics-stage-label">
                    <span>{info?.name ?? stage}</span>
                    {info?.detail && <AccessibleTooltip text={info.detail} label={`${info.name} help`} />}
                  </div>
                  {dates.map((date) => {
                    const entry = lookup.get(`${stage}|${date}`);
                    if (!entry) return <HeatmapCell key={date} label={`${stage} ${date} ${t('analytics.noDataMark')}`} text="—" />;
                    if (entry.hasRate) {
                      const intensity = rateIntensity(entry.interventionRate);
                      return <HeatmapCell key={date} label={`${stage} ${date} ${(entry.interventionRate * 100).toFixed(1)}% (${entry.count})`}
                        text={`${(entry.interventionRate * 100).toFixed(0)}%`}
                        color={`rgba(255, ${Math.round(199 - intensity * 140)}, ${Math.round(89 - intensity * 50)}, ${0.18 + intensity * 0.7})`} />;
                    }
                    const intensity = countIntensity(entry.count);
                    return <HeatmapCell key={date} label={`${stage} ${date} ${entry.count} ${t('analytics.countUnit')}`}
                      text={String(entry.count)} color={`rgba(88, 86, 214, ${0.12 + intensity * 0.68})`} />;
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
      <div className="analytics-heatmap-legends">
        <span>{t('analytics.rateScaleLegend')}</span><i className="analytics-rate-legend" />
        <span>{t('analytics.countScaleLegend')}</span><i className="analytics-count-legend" />
      </div>
    </div>
  );
}

function HeatmapCell({ label, text, color = 'var(--surface-2)' }: { label: string; text: string; color?: string }) {
  return <button type="button" role="gridcell" className="analytics-heatmap-cell" aria-label={label}
    data-tooltip={label} style={{ background: color }}>{text}</button>;
}

function AccessibleTooltip({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  const id = React.useId();
  return (
    <span className="analytics-tooltip" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label={label} aria-expanded={open} aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => { if (event.key === 'Escape') { setOpen(false); event.currentTarget.blur(); } }}>?</button>
      {open && <span id={id} role="tooltip">{text}</span>}
    </span>
  );
}

function ReportStatusBadge({ status, t }: { status: string; t: (key: string) => string }) {
  const key: Record<string, string> = {
    completed: 'analytics.reportStatusCompleted', partial: 'analytics.reportStatusPartial',
    failed: 'analytics.reportStatusFailed', generating: 'analytics.reportStatusGenerating',
    cancelled: 'analytics.reportStatusCancelled',
  };
  return <span className={`status-badge status-${status}`}>{status === 'generating' && <span className="loading-spinner" />}{t(key[status] ?? status)}</span>;
}

function CardError({ title, detail }: { title: string; detail: string }) {
  return <div role="alert" className="card analytics-card-error"><strong>{title}</strong><span>{detail}</span></div>;
}
function CardLoading({ bare = false }: { bare?: boolean }) {
  return <div className={bare ? 'analytics-card-loading' : 'card analytics-card-loading'}><span className="loading-spinner" /></div>;
}
function EmptyCard({ text }: { text: string }) {
  return <div className="card analytics-empty-card">{text}</div>;
}
